import React, { useState, useEffect } from 'react';
import { 
  Search, Plus, Users as UsersIcon, Shield, X, Check,
  AlertCircle, Mail, Loader2, ChevronDown, User, Trash2,
  BarChart3, Link2, Briefcase, PoundSterling, Calendar, ChevronRight,
  Eye, EyeOff, Key, Edit3, AlertTriangle
} from 'lucide-react';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Switch } from '../components/ui/switch';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Role colors for badges
const ROLE_COLORS = {
  'Owner': 'bg-purple-100 text-purple-700 border-purple-200',
  'Administrator': 'bg-blue-100 text-blue-700 border-blue-200',
  'Creator': 'bg-emerald-100 text-emerald-700 border-emerald-200',
  'Job Editor': 'bg-amber-100 text-amber-700 border-amber-200',
  'Reporting': 'bg-cyan-100 text-cyan-700 border-cyan-200',
  'Planner': 'bg-indigo-100 text-indigo-700 border-indigo-200',
  'Worker': 'bg-zinc-100 text-zinc-700 border-zinc-200',
  'Marketing': 'bg-pink-100 text-pink-700 border-pink-200',
  'Canvasser': 'bg-orange-100 text-orange-700 border-orange-200',
};

const UserCard = ({ user, onViewStats, onToggleActive, isUpdating }) => {
  const initials = user.name?.split(' ').map(n => n[0]).join('').toUpperCase() || '?';
  const roleColor = ROLE_COLORS[user.role] || ROLE_COLORS['Worker'];
  
  return (
    <div 
      className="bg-white border-b border-gray-100 p-4 hover:bg-gray-50 transition-colors cursor-pointer"
      onClick={() => onViewStats(user)}
      data-testid={`user-card-${user.id}`}
    >
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 rounded-full bg-zinc-800 flex items-center justify-center text-white font-semibold">
            {initials}
          </div>
          <div>
            <div className="flex items-center gap-2">
              <h3 className="font-semibold text-gray-900">{user.name}</h3>
              {user.is_owner && (
                <Badge className="bg-yellow-100 text-yellow-700 text-xs">Owner</Badge>
              )}
            </div>
            <p className="text-sm text-gray-500">{user.email}</p>
            {user.linked_assignees && user.linked_assignees.length > 0 && (
              <div className="flex items-center gap-1 mt-1">
                <Link2 size={10} className="text-blue-500" />
                <span className="text-xs text-blue-600">
                  Linked: {user.linked_assignees.join(', ')}
                </span>
              </div>
            )}
          </div>
        </div>
        <div className="flex items-center gap-3">
          <Badge className={`${roleColor} border`}>
            <Shield size={12} className="mr-1" />
            {user.role}
          </Badge>
          {!user.is_owner && (
            <div className="flex items-center gap-2">
              <span className={`text-xs ${user.is_active ? 'text-green-600' : 'text-gray-400'}`}>
                {user.is_active ? 'Active' : 'Inactive'}
              </span>
              <Switch
                checked={user.is_active}
                onCheckedChange={(checked) => onToggleActive(user.id, checked)}
                disabled={isUpdating}
                onClick={(e) => e.stopPropagation()}
              />
            </div>
          )}
          <ChevronRight size={18} className="text-gray-400" />
        </div>
      </div>
    </div>
  );
};

const UserStatsModal = ({ user, onClose, assigneeOptions, onRefresh, roles, onEdit }) => {
  const [stats, setStats] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [linkedAssignees, setLinkedAssignees] = useState([]);
  const [isSavingLinks, setIsSavingLinks] = useState(false);
  const [showAssigneeDropdown, setShowAssigneeDropdown] = useState(false);
  const [showResetPassword, setShowResetPassword] = useState(false);
  const [newPassword, setNewPassword] = useState('');
  const [isResettingPassword, setIsResettingPassword] = useState(false);
  const [passwordError, setPasswordError] = useState('');
  const [passwordSuccess, setPasswordSuccess] = useState('');

  // Delete flow state
  const [showDeleteFlow, setShowDeleteFlow] = useState(false);
  const [deleteImpact, setDeleteImpact] = useState(null);
  const [isLoadingImpact, setIsLoadingImpact] = useState(false);
  const [jobAction, setJobAction] = useState('unassign');
  const [reassignTo, setReassignTo] = useState('');
  const [confirmName, setConfirmName] = useState('');
  const [isDeleting, setIsDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState('');

  useEffect(() => {
    fetchUserStats();
    fetchLinkedAssignees();
  }, [user.id]);

  const fetchUserStats = async () => {
    setIsLoading(true);
    try {
      const token = localStorage.getItem('streakfree_token');
      const res = await fetch(`${API_URL}/api/users/${user.id}/stats`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setStats(data);
      }
    } catch (err) {
      console.error('Error fetching stats:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchLinkedAssignees = async () => {
    try {
      const token = localStorage.getItem('streakfree_token');
      const res = await fetch(`${API_URL}/api/users/${user.id}/linked-assignees`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setLinkedAssignees(data.assignees || []);
      }
    } catch (err) {
      console.error('Error fetching linked assignees:', err);
    }
  };

  const handleResetPassword = async () => {
    if (!newPassword.trim()) {
      setPasswordError('Password is required');
      return;
    }
    if (newPassword.length < 6) {
      setPasswordError('Password must be at least 6 characters');
      return;
    }

    setIsResettingPassword(true);
    setPasswordError('');
    setPasswordSuccess('');

    try {
      const token = localStorage.getItem('streakfree_token');
      const res = await fetch(`${API_URL}/api/team/users/${user.id}/reset-password`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ password: newPassword })
      });

      if (res.ok) {
        setPasswordSuccess('Password updated successfully!');
        setNewPassword('');
        setShowResetPassword(false);
        setTimeout(() => setPasswordSuccess(''), 3000);
      } else {
        const data = await res.json();
        setPasswordError(data.detail || 'Failed to reset password');
      }
    } catch (err) {
      setPasswordError('Error resetting password');
    } finally {
      setIsResettingPassword(false);
    }
  };

  const openDeleteFlow = async () => {
    setShowDeleteFlow(true);
    setIsLoadingImpact(true);
    setDeleteError('');
    setJobAction('unassign');
    setReassignTo('');
    setConfirmName('');
    try {
      const token = localStorage.getItem('streakfree_token');
      const res = await fetch(`${API_URL}/api/team/users/${user.id}/delete-impact`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setDeleteImpact(data);
      } else {
        const err = await res.json();
        setDeleteError(err.detail || 'Failed to load impact');
      }
    } catch (err) {
      setDeleteError('Failed to load impact');
    } finally {
      setIsLoadingImpact(false);
    }
  };

  const handleDelete = async () => {
    if (confirmName.trim().toLowerCase() !== (user.name || '').toLowerCase()) {
      setDeleteError(`Type "${user.name}" to confirm`);
      return;
    }
    if (jobAction === 'reassign' && !reassignTo) {
      setDeleteError('Please pick a worker to reassign jobs to');
      return;
    }

    setIsDeleting(true);
    setDeleteError('');
    try {
      const token = localStorage.getItem('streakfree_token');
      const params = new URLSearchParams({ job_action: jobAction });
      if (jobAction === 'reassign') params.append('reassign_to', reassignTo);

      const res = await fetch(`${API_URL}/api/team/users/${user.id}?${params.toString()}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (res.ok) {
        const data = await res.json();
        let msg = `${user.name} deleted.`;
        if (data.jobs_unassigned) msg += ` ${data.jobs_unassigned} job(s) unassigned.`;
        if (data.jobs_reassigned) msg += ` ${data.jobs_reassigned} job(s) reassigned to ${data.reassigned_to}.`;
        if (data.jobs_deleted) msg += ` ${data.jobs_deleted} job(s) deleted.`;
        // Use alert as a quick non-blocking confirmation
        alert(msg);
        await onRefresh();
        onClose();
      } else {
        const err = await res.json();
        setDeleteError(err.detail || 'Delete failed');
      }
    } catch (err) {
      setDeleteError('Delete failed');
    } finally {
      setIsDeleting(false);
    }
  };

  const handleSaveLinks = async () => {
    setIsSavingLinks(true);
    try {
      const token = localStorage.getItem('streakfree_token');
      const res = await fetch(`${API_URL}/api/users/${user.id}/link-assignees`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ assignees: linkedAssignees })
      });
      if (res.ok) {
        await fetchUserStats();
        onRefresh();
      }
    } catch (err) {
      console.error('Error saving links:', err);
    } finally {
      setIsSavingLinks(false);
    }
  };

  const toggleAssignee = (assignee) => {
    if (linkedAssignees.includes(assignee)) {
      setLinkedAssignees(linkedAssignees.filter(a => a !== assignee));
    } else {
      setLinkedAssignees([...linkedAssignees, assignee]);
    }
  };

  const roleColor = ROLE_COLORS[user.role] || ROLE_COLORS['Worker'];
  const initials = user.name?.split(' ').map(n => n[0]).join('').toUpperCase() || '?';

  return (
    <div className="fixed inset-0 bg-black/50 flex items-start sm:items-center justify-center z-50 p-2 sm:p-4 pt-16 sm:pt-4 overflow-y-auto">
      <div className="bg-white rounded-2xl w-full max-w-lg shadow-xl modal-shell-h overflow-hidden flex flex-col my-auto">
        {/* Header - Compact */}
        <div className="flex items-center justify-between p-3 sm:p-4 border-b border-gray-100 flex-shrink-0">
          <div className="flex items-center gap-2 sm:gap-3">
            <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-full bg-zinc-800 flex items-center justify-center text-white font-semibold text-sm sm:text-base">
              {initials}
            </div>
            <div>
              <h2 className="text-base sm:text-lg font-semibold text-gray-900">{user.name}</h2>
              <Badge className={`${roleColor} border text-xs`}>
                <Shield size={10} className="mr-1" />
                {user.role}
              </Badge>
            </div>
          </div>
          <button 
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
            data-testid="close-stats-modal"
          >
            <X size={20} className="text-gray-500" />
          </button>
        </div>

        {/* Action bar - Edit / Delete (hidden for Owner) */}
        {user.role !== 'Owner' && !showDeleteFlow && (
          <div className="flex items-center gap-2 px-3 sm:px-4 py-2 bg-gray-50 border-b border-gray-100 flex-shrink-0">
            <Button
              onClick={() => onEdit && onEdit(user)}
              variant="outline"
              size="sm"
              className="flex-1"
              data-testid="edit-user-btn"
            >
              <Edit3 size={14} className="mr-2" />
              Edit user
            </Button>
            <Button
              onClick={openDeleteFlow}
              variant="outline"
              size="sm"
              className="flex-1 border-red-200 text-red-600 hover:bg-red-50 hover:text-red-700"
              data-testid="open-delete-user-btn"
            >
              <Trash2 size={14} className="mr-2" />
              Delete user
            </Button>
          </div>
        )}

        {/* Delete flow takes over modal body when active */}
        {showDeleteFlow ? (
          <div className="flex-1 overflow-y-auto p-4 space-y-4" data-testid="delete-user-flow">
            <button
              onClick={() => { setShowDeleteFlow(false); setDeleteError(''); }}
              className="text-sm text-gray-500 hover:text-gray-700 flex items-center gap-1"
            >
              ← Cancel delete
            </button>

            <div className="bg-red-50 border border-red-200 rounded-xl p-4">
              <div className="flex items-start gap-3">
                <AlertTriangle size={20} className="text-red-600 mt-0.5 flex-shrink-0" />
                <div className="flex-1">
                  <h3 className="font-semibold text-red-900">Delete {user.name}?</h3>
                  <p className="text-sm text-red-700 mt-1">
                    This will permanently remove their login and account. This cannot be undone.
                  </p>
                </div>
              </div>
            </div>

            {isLoadingImpact ? (
              <div className="flex items-center justify-center h-24">
                <Loader2 className="w-6 h-6 animate-spin text-red-500" />
              </div>
            ) : deleteImpact ? (
              <>
                <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
                  <p className="text-sm font-medium text-amber-900">
                    {deleteImpact.affected_jobs > 0
                      ? `${deleteImpact.affected_jobs} job${deleteImpact.affected_jobs !== 1 ? 's' : ''} are currently assigned to this user`
                      : 'No jobs are assigned to this user'}
                  </p>
                  {deleteImpact.linked_assignees && deleteImpact.linked_assignees.length > 0 && (
                    <p className="text-xs text-amber-700 mt-1">
                      Assignee names: {deleteImpact.linked_assignees.join(', ')}
                    </p>
                  )}
                </div>

                {deleteImpact.affected_jobs > 0 && (
                  <div className="bg-white border border-gray-200 rounded-xl p-4">
                    <h4 className="font-medium text-gray-900 mb-3">What should happen to their jobs?</h4>
                    <div className="space-y-2">
                      {[
                        { value: 'unassign', label: 'Mark as Unassigned', desc: 'Keep the jobs, clear the assignee' },
                        { value: 'reassign', label: 'Reassign to another worker', desc: 'Move the jobs to someone else' },
                        { value: 'delete', label: 'Delete the jobs too', desc: '⚠ Permanently removes all their jobs' }
                      ].map(opt => (
                        <label
                          key={opt.value}
                          className={`flex items-start gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${
                            jobAction === opt.value
                              ? 'border-red-300 bg-red-50'
                              : 'border-gray-200 hover:bg-gray-50'
                          }`}
                          data-testid={`job-action-${opt.value}`}
                        >
                          <input
                            type="radio"
                            checked={jobAction === opt.value}
                            onChange={() => setJobAction(opt.value)}
                            className="mt-1"
                          />
                          <div className="flex-1">
                            <p className="text-sm font-medium text-gray-900">{opt.label}</p>
                            <p className="text-xs text-gray-500">{opt.desc}</p>
                          </div>
                        </label>
                      ))}
                    </div>

                    {jobAction === 'reassign' && (
                      <div className="mt-3">
                        <label className="block text-sm font-medium text-gray-700 mb-1">Reassign to</label>
                        <select
                          value={reassignTo}
                          onChange={(e) => setReassignTo(e.target.value)}
                          className="w-full px-3 py-2 border border-gray-200 rounded-lg bg-white"
                          data-testid="reassign-to-select"
                        >
                          <option value="">Choose a worker…</option>
                          {(deleteImpact.available_workers || []).map(w => (
                            <option key={w.id} value={w.name}>{w.name} ({w.role})</option>
                          ))}
                        </select>
                        {deleteImpact.available_workers?.length === 0 && (
                          <p className="text-xs text-amber-600 mt-1">No other active workers available</p>
                        )}
                      </div>
                    )}
                  </div>
                )}

                <div className="bg-white border border-gray-200 rounded-xl p-4">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Type <span className="font-semibold">{user.name}</span> to confirm
                  </label>
                  <Input
                    value={confirmName}
                    onChange={(e) => setConfirmName(e.target.value)}
                    placeholder={user.name}
                    data-testid="confirm-name-input"
                  />
                </div>

                {deleteError && (
                  <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
                    {deleteError}
                  </div>
                )}

                <Button
                  onClick={handleDelete}
                  disabled={isDeleting || confirmName.trim().toLowerCase() !== (user.name || '').toLowerCase()}
                  className="w-full bg-red-600 hover:bg-red-700"
                  data-testid="confirm-delete-user-btn"
                >
                  {isDeleting ? (
                    <><Loader2 size={16} className="animate-spin mr-2" /> Deleting…</>
                  ) : (
                    <><Trash2 size={16} className="mr-2" /> Permanently delete user</>
                  )}
                </Button>
              </>
            ) : deleteError ? (
              <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
                {deleteError}
              </div>
            ) : null}
          </div>
        ) : (
        <>
        {/* Content */}
        <div className="flex-1 overflow-y-auto p-4 space-y-4">
          {isLoading ? (
            <div className="flex items-center justify-center h-40">
              <Loader2 className="w-8 h-8 animate-spin text-emerald-500" />
            </div>
          ) : (
            <>
              {/* Link Assignees Section */}
              <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
                <h3 className="font-medium text-blue-900 mb-2 flex items-center gap-2">
                  <Link2 size={16} />
                  Link to Assignees
                </h3>
                <p className="text-sm text-blue-700 mb-3">
                  Link this user to assignee names so they only see jobs assigned to them.
                </p>
                
                {/* Assignee Selection */}
                <div className="relative">
                  <button
                    onClick={() => setShowAssigneeDropdown(!showAssigneeDropdown)}
                    className="w-full flex items-center justify-between px-3 py-2 border border-blue-200 rounded-lg bg-white hover:border-blue-300 transition-colors"
                    data-testid="assignee-dropdown-btn"
                  >
                    <span className="text-sm text-gray-700">
                      {linkedAssignees.length > 0 
                        ? `${linkedAssignees.length} assignee(s) selected`
                        : 'Select assignees...'}
                    </span>
                    <ChevronDown size={18} className="text-gray-400" />
                  </button>
                  
                  {showAssigneeDropdown && (
                    <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg z-10 max-h-48 overflow-y-auto">
                      {assigneeOptions.length === 0 ? (
                        <div className="px-3 py-2 text-sm text-gray-500">No assignees found in jobs</div>
                      ) : (
                        assigneeOptions.map((assignee) => (
                          <button
                            key={assignee.name}
                            onClick={() => toggleAssignee(assignee.name)}
                            className="w-full px-3 py-2 text-left hover:bg-gray-50 flex items-center justify-between"
                            data-testid={`assignee-option-${assignee.name}`}
                          >
                            <span className="text-sm text-gray-700">
                              {assignee.name} <span className="text-gray-400">({assignee.job_count} jobs)</span>
                            </span>
                            {linkedAssignees.includes(assignee.name) && (
                              <Check size={16} className="text-emerald-600" />
                            )}
                          </button>
                        ))
                      )}
                    </div>
                  )}
                </div>
                
                {/* Selected Assignees */}
                {linkedAssignees.length > 0 && (
                  <div className="flex flex-wrap gap-2 mt-3">
                    {linkedAssignees.map(a => (
                      <Badge 
                        key={a} 
                        className="bg-blue-100 text-blue-700 cursor-pointer"
                        onClick={() => toggleAssignee(a)}
                      >
                        {a} <X size={12} className="ml-1" />
                      </Badge>
                    ))}
                  </div>
                )}
                
                <Button 
                  onClick={handleSaveLinks}
                  disabled={isSavingLinks}
                  size="sm"
                  className="mt-3 bg-blue-600 hover:bg-blue-700"
                  data-testid="save-assignees-btn"
                >
                  {isSavingLinks ? (
                    <Loader2 size={14} className="animate-spin mr-2" />
                  ) : (
                    <Check size={14} className="mr-2" />
                  )}
                  Save Links
                </Button>
              </div>
              
              {/* Performance Stats */}
              <div className="bg-gray-50 rounded-xl p-4">
                <h3 className="font-medium text-gray-900 mb-3 flex items-center gap-2">
                  <BarChart3 size={16} />
                  Performance Stats
                </h3>
                
                <div className="grid grid-cols-2 gap-3">
                  <div className="bg-white rounded-lg p-3 border border-gray-200">
                    <div className="flex items-center gap-2 text-emerald-600 mb-1">
                      <PoundSterling size={14} />
                      <span className="text-xs font-medium uppercase">Total Revenue</span>
                    </div>
                    <p className="text-xl font-bold text-gray-900">
                      £{(stats?.total_revenue || 0).toFixed(2)}
                    </p>
                  </div>
                  
                  <div className="bg-white rounded-lg p-3 border border-gray-200">
                    <div className="flex items-center gap-2 text-blue-600 mb-1">
                      <Briefcase size={14} />
                      <span className="text-xs font-medium uppercase">Total Jobs</span>
                    </div>
                    <p className="text-xl font-bold text-gray-900">
                      {stats?.total_jobs || 0}
                    </p>
                  </div>
                  
                  <div className="bg-white rounded-lg p-3 border border-gray-200">
                    <div className="flex items-center gap-2 text-purple-600 mb-1">
                      <Calendar size={14} />
                      <span className="text-xs font-medium uppercase">This Month</span>
                    </div>
                    <p className="text-xl font-bold text-gray-900">
                      £{(stats?.monthly_revenue || 0).toFixed(2)}
                    </p>
                    <p className="text-xs text-gray-500">{stats?.jobs_this_month || 0} jobs</p>
                  </div>
                  
                  <div className="bg-white rounded-lg p-3 border border-gray-200">
                    <div className="flex items-center gap-2 text-amber-600 mb-1">
                      <Check size={14} />
                      <span className="text-xs font-medium uppercase">Completed</span>
                    </div>
                    <p className="text-xl font-bold text-gray-900">
                      {stats?.completed_jobs || 0}
                    </p>
                  </div>
                </div>
              </div>
              
              {/* Reset Password Section - Only for Workers, not Owner */}
              {user.role !== 'Owner' && (
                <div className="bg-gray-50 rounded-xl p-4">
                  <h3 className="font-medium text-gray-900 mb-3 flex items-center gap-2">
                    <Key size={16} />
                    Account Security
                  </h3>
                  
                  {passwordSuccess && (
                    <div className="mb-3 p-2 bg-emerald-50 border border-emerald-200 rounded-lg text-sm text-emerald-700">
                      {passwordSuccess}
                    </div>
                  )}
                  
                  {!showResetPassword ? (
                    <Button
                      onClick={() => setShowResetPassword(true)}
                      variant="outline"
                      size="sm"
                      className="w-full"
                      data-testid="reset-password-btn"
                    >
                      <Key size={14} className="mr-2" />
                      Set / Reset Password
                    </Button>
                  ) : (
                    <div className="space-y-3">
                      <div>
                        <Input
                          type="password"
                          value={newPassword}
                          onChange={(e) => setNewPassword(e.target.value)}
                          placeholder="Enter new password (min 6 characters)"
                          data-testid="new-password-input"
                        />
                        {passwordError && (
                          <p className="text-xs text-red-500 mt-1">{passwordError}</p>
                        )}
                      </div>
                      <div className="flex gap-2">
                        <Button
                          onClick={handleResetPassword}
                          disabled={isResettingPassword}
                          size="sm"
                          className="flex-1 bg-emerald-600 hover:bg-emerald-700"
                          data-testid="save-password-btn"
                        >
                          {isResettingPassword ? (
                            <Loader2 size={14} className="animate-spin mr-2" />
                          ) : (
                            <Check size={14} className="mr-2" />
                          )}
                          Save Password
                        </Button>
                        <Button
                          onClick={() => {
                            setShowResetPassword(false);
                            setNewPassword('');
                            setPasswordError('');
                          }}
                          variant="outline"
                          size="sm"
                        >
                          Cancel
                        </Button>
                      </div>
                      <p className="text-xs text-gray-500">
                        Login: <strong>{user.email}</strong>
                      </p>
                    </div>
                  )}
                </div>
              )}
              
              {/* Recent Jobs */}
              {stats?.recent_jobs && stats.recent_jobs.length > 0 && (
                <div className="bg-gray-50 rounded-xl p-4">
                  <h3 className="font-medium text-gray-900 mb-3">Recent Jobs</h3>
                  <div className="space-y-2">
                    {stats.recent_jobs.map((job, idx) => (
                      <div key={job.id || idx} className="bg-white rounded-lg p-2 border border-gray-200 text-sm">
                        <div className="flex justify-between">
                          <span className="font-medium text-gray-900">{job.customer_name || job.title || 'Job'}</span>
                          <span className="text-emerald-600 font-medium">£{(job.price || 0).toFixed(2)}</span>
                        </div>
                        <div className="flex justify-between text-xs text-gray-500 mt-1">
                          <span>{job.scheduled_date?.split('T')[0] || 'N/A'}</span>
                          <Badge className={job.status === 'Complete' ? 'bg-emerald-100 text-emerald-700' : 'bg-gray-100 text-gray-700'}>
                            {job.status || 'Pending'}
                          </Badge>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </>
          )}
        </div>
        </>
        )}
      </div>
    </div>
  );
};

const AddUserModal = ({ onClose, onSave, maxUsers, currentActive, roles, pricePerWorker }) => {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [role, setRole] = useState('Worker');
  const [isActive, setIsActive] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState('');
  const [showRoleDropdown, setShowRoleDropdown] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [inviteUrl, setInviteUrl] = useState('');
  const [creatingInvite, setCreatingInvite] = useState(false);
  const [inviteCopied, setInviteCopied] = useState(false);

  const canAddActive = currentActive < maxUsers;

  const createInvite = async () => {
    setCreatingInvite(true);
    setError('');
    try {
      const token = localStorage.getItem('streakfree_token');
      const res = await fetch(`${API_URL}/api/team/invites`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ role }),
      });
      const d = await res.json();
      if (!res.ok) throw new Error(typeof d.detail === 'string' ? d.detail : 'Failed to create invite');
      setInviteUrl(`${window.location.origin}/join/${d.token}`);
    } catch (err) {
      setError(err.message);
    } finally {
      setCreatingInvite(false);
    }
  };

  const copyInvite = () => {
    navigator.clipboard.writeText(inviteUrl);
    setInviteCopied(true);
    setTimeout(() => setInviteCopied(false), 2000);
  };

  const handleSave = async () => {
    if (!name.trim()) {
      setError('Name is required');
      return;
    }
    if (!email.trim()) {
      setError('Email is required');
      return;
    }
    if (!password.trim()) {
      setError('Password is required');
      return;
    }
    if (password.length < 6) {
      setError('Password must be at least 6 characters');
      return;
    }
    
    setIsSaving(true);
    setError('');
    
    try {
      await onSave({ name, email, password, role, is_active: isActive && canAddActive });
      onClose();
    } catch (err) {
      setError(err.message || 'Failed to add user');
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl w-full max-w-md shadow-xl">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-100">
          <h2 className="text-lg font-semibold text-gray-900">Add New User</h2>
          <button 
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <X size={20} className="text-gray-500" />
          </button>
        </div>
        
        {/* User Limit Warning */}
        {!canAddActive && (
          <div className="mx-4 mt-4 p-3 bg-amber-50 border border-amber-200 rounded-xl">
            <div className="flex items-start gap-2">
              <AlertCircle size={18} className="text-amber-600 mt-0.5 flex-shrink-0" />
              <div className="text-sm text-amber-800">
                <p className="font-medium">User limit reached</p>
                <p className="text-amber-700 mt-1">
                  You currently have {currentActive} of {maxUsers} users active. 
                  Increase your user limit (£{(pricePerWorker || 10).toFixed(2)}/user/month) or deactivate another user.
                </p>
              </div>
            </div>
          </div>
        )}
        
        {/* Form */}
        <div className="p-4 space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Name</label>
            <Input
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Enter full name"
              data-testid="new-user-name"
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
            <Input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="Enter email address"
              data-testid="new-user-email"
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Password</label>
            <div className="relative">
              <Input
                type={showPassword ? "text" : "password"}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Enter password (min 6 characters)"
                data-testid="new-user-password"
                className="pr-10"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
              >
                {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
              </button>
            </div>
            <p className="text-xs text-gray-500 mt-1">This will be the user's login password</p>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Role</label>
            <div className="relative">
              <button
                onClick={() => setShowRoleDropdown(!showRoleDropdown)}
                className="w-full flex items-center justify-between px-3 py-2 border border-gray-200 rounded-lg bg-white hover:border-gray-300 transition-colors"
              >
                <span className="flex items-center gap-2">
                  <Badge className={`${ROLE_COLORS[role]} border text-xs`}>
                    <Shield size={10} className="mr-1" />
                    {role}
                  </Badge>
                </span>
                <ChevronDown size={18} className="text-gray-400" />
              </button>
              
              {showRoleDropdown && (
                <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg z-10 max-h-60 overflow-y-auto">
                  {Object.entries(roles).map(([roleName, roleInfo]) => (
                    <button
                      key={roleName}
                      onClick={() => {
                        setRole(roleName);
                        setShowRoleDropdown(false);
                      }}
                      className="w-full px-3 py-2 text-left hover:bg-gray-50 flex items-center justify-between"
                    >
                      <div>
                        <Badge className={`${ROLE_COLORS[roleName]} border text-xs`}>
                          <Shield size={10} className="mr-1" />
                          {roleName}
                        </Badge>
                        <p className="text-xs text-gray-500 mt-1">{roleInfo.description}</p>
                      </div>
                      {role === roleName && <Check size={16} className="text-emerald-600" />}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
          
          <div className="flex items-center justify-between p-3 bg-gray-50 rounded-xl">
            <div>
              <p className="font-medium text-gray-900">Active</p>
              <p className="text-xs text-gray-500">User can log in and access the system</p>
            </div>
            <Switch
              checked={isActive && canAddActive}
              onCheckedChange={setIsActive}
              disabled={!canAddActive}
            />
          </div>

          {/* Invite link alternative — no password sharing needed */}
          <div className="p-3 bg-sky-50 border border-sky-200 rounded-xl">
            {!inviteUrl ? (
              <div className="flex items-center justify-between gap-3">
                <p className="text-xs text-sky-800">
                  Or send a <span className="font-semibold">one-tap invite link</span> — they set their own
                  name and password, joining as <span className="font-semibold">{role}</span>.
                </p>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={createInvite}
                  disabled={creatingInvite || !canAddActive}
                  className="border-sky-300 text-sky-700 hover:bg-sky-100 flex-shrink-0"
                  data-testid="create-invite-link-btn"
                >
                  {creatingInvite ? <Loader2 size={14} className="animate-spin" /> : 'Create invite link'}
                </Button>
              </div>
            ) : (
              <div>
                <p className="text-xs font-semibold text-sky-800 mb-1">Share this link with your {role.toLowerCase()} (valid 7 days, single use):</p>
                <div className="flex items-center gap-2">
                  <code className="flex-1 text-xs bg-white border border-sky-200 rounded-lg px-2 py-1.5 text-sky-700 break-all" data-testid="invite-link-url">{inviteUrl}</code>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={copyInvite}
                    className="border-sky-300 text-sky-700 hover:bg-sky-100 flex-shrink-0"
                    data-testid="copy-invite-link-btn"
                  >
                    {inviteCopied ? <Check size={14} className="text-emerald-600" /> : 'Copy'}
                  </Button>
                </div>
              </div>
            )}
          </div>
          
          {error && (
            <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
              {error}
            </div>
          )}
        </div>
        
        {/* Footer */}
        <div className="flex gap-3 p-4 border-t border-gray-100">
          <Button variant="outline" onClick={onClose} className="flex-1">
            Cancel
          </Button>
          <Button 
            onClick={handleSave} 
            disabled={isSaving}
            className="flex-1 bg-emerald-600 hover:bg-emerald-700"
          >
            {isSaving ? (
              <>
                <Loader2 size={16} className="animate-spin mr-2" />
                Adding...
              </>
            ) : (
              <>
                <Plus size={16} className="mr-2" />
                Add User
              </>
            )}
          </Button>
        </div>
      </div>
    </div>
  );
};


const EditUserModal = ({ user, onClose, onSave, roles }) => {
  const [name, setName] = useState(user.name || '');
  const [email, setEmail] = useState(user.email || '');
  const [role, setRole] = useState(user.role || 'Worker');
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState('');
  const [showRoleDropdown, setShowRoleDropdown] = useState(false);

  const handleSave = async () => {
    if (!name.trim()) { setError('Name is required'); return; }
    if (!email.trim()) { setError('Email is required'); return; }
    setIsSaving(true);
    setError('');
    try {
      await onSave({ name: name.trim(), email: email.trim().toLowerCase(), role });
      onClose();
    } catch (err) {
      setError(err.message || 'Failed to update user');
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[60] p-4">
      <div className="bg-white rounded-2xl w-full max-w-md shadow-xl">
        <div className="flex items-center justify-between p-4 border-b border-gray-100">
          <h2 className="text-lg font-semibold text-gray-900">Edit User</h2>
          <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-lg" data-testid="edit-modal-close">
            <X size={20} className="text-gray-500" />
          </button>
        </div>

        <div className="p-4 space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Name</label>
            <Input
              value={name}
              onChange={(e) => setName(e.target.value)}
              data-testid="edit-user-name"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
            <Input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              data-testid="edit-user-email"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Role</label>
            <div className="relative">
              <button
                onClick={() => setShowRoleDropdown(!showRoleDropdown)}
                className="w-full flex items-center justify-between px-3 py-2 border border-gray-200 rounded-lg bg-white hover:border-gray-300 transition-colors"
                data-testid="edit-user-role-toggle"
              >
                <Badge className={`${ROLE_COLORS[role]} border text-xs`}>
                  <Shield size={10} className="mr-1" />
                  {role}
                </Badge>
                <ChevronDown size={18} className="text-gray-400" />
              </button>
              {showRoleDropdown && (
                <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg z-10 max-h-60 overflow-y-auto">
                  {Object.entries(roles || {}).map(([roleName, roleInfo]) => (
                    <button
                      key={roleName}
                      onClick={() => { setRole(roleName); setShowRoleDropdown(false); }}
                      className="w-full px-3 py-2 text-left hover:bg-gray-50 flex items-center justify-between"
                    >
                      <div>
                        <Badge className={`${ROLE_COLORS[roleName]} border text-xs`}>
                          <Shield size={10} className="mr-1" />
                          {roleName}
                        </Badge>
                        <p className="text-xs text-gray-500 mt-1">{roleInfo.description}</p>
                      </div>
                      {role === roleName && <Check size={16} className="text-emerald-600" />}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>

          {error && (
            <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
              {error}
            </div>
          )}

          <p className="text-xs text-gray-500">
            To change the password, use "Set / Reset Password" in the user details modal.
          </p>
        </div>

        <div className="flex gap-3 p-4 border-t border-gray-100">
          <Button variant="outline" onClick={onClose} className="flex-1">Cancel</Button>
          <Button
            onClick={handleSave}
            disabled={isSaving}
            className="flex-1 bg-emerald-600 hover:bg-emerald-700"
            data-testid="save-edit-user-btn"
          >
            {isSaving ? <><Loader2 size={16} className="animate-spin mr-2" /> Saving…</> : <><Check size={16} className="mr-2" /> Save changes</>}
          </Button>
        </div>
      </div>
    </div>
  );
};


const UsersAndTeams = () => {
  const [users, setUsers] = useState([]);
  const [roles, setRoles] = useState({});
  const [isLoading, setIsLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [activeFilter, setActiveFilter] = useState('active');
  const [showAddModal, setShowAddModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [editingUser, setEditingUser] = useState(null);
  const [updatingUser, setUpdatingUser] = useState(null);
  const [assigneeOptions, setAssigneeOptions] = useState([]);
  const [subscription, setSubscription] = useState({
    tier: 'starter',
    max_users: 10,
    total_users: 1,
    active_users: 1,
    active_workers: 0,
    base_price: 10.00,
    price_per_worker: 10.00,
    total_monthly_cost: 10.00
  });

  // Fetch users, roles, and assignees
  useEffect(() => {
    const fetchData = async () => {
      setIsLoading(true);
      try {
        const token = localStorage.getItem('streakfree_token');
        
        // Fetch team users
        const usersRes = await fetch(`${API_URL}/api/team/users`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        
        // Fetch roles
        const rolesRes = await fetch(`${API_URL}/api/team/roles`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        
        // Fetch assignees for linking
        const assigneesRes = await fetch(`${API_URL}/api/jobs/assignees`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        
        if (usersRes.ok) {
          const data = await usersRes.json();
          setUsers(data.users || []);
          setSubscription({
            tier: data.tier,
            max_users: data.max_users,
            total_users: data.total_users,
            active_users: data.active_users,
            active_workers: data.active_workers || 0,
            base_price: data.base_price || 10.00,
            price_per_worker: data.price_per_worker || 10.00,
            total_monthly_cost: data.total_monthly_cost || data.base_price + (data.active_workers * (data.price_per_worker || 10.00))
          });
        }
        
        if (rolesRes.ok) {
          const data = await rolesRes.json();
          setRoles(data.roles || {});
        }
        
        if (assigneesRes.ok) {
          const data = await assigneesRes.json();
          // API returns array directly: [{name: "Sam", job_count: 177}, ...]
          setAssigneeOptions(Array.isArray(data) ? data : (data.assignees || []));
        }
      } catch (err) {
        console.error('Error fetching users:', err);
      } finally {
        setIsLoading(false);
      }
    };
    
    fetchData();
  }, []);

  const refreshData = async () => {
    const token = localStorage.getItem('streakfree_token');
    const usersRes = await fetch(`${API_URL}/api/team/users`, {
      headers: { 'Authorization': `Bearer ${token}` }
    });
    if (usersRes.ok) {
      const data = await usersRes.json();
      setUsers(data.users || []);
      setSubscription({
        tier: data.tier,
        max_users: data.max_users,
        total_users: data.total_users,
        active_users: data.active_users,
        active_workers: data.active_workers || 0,
        base_price: data.base_price || 10.00,
        price_per_worker: data.price_per_worker || 10.00,
        total_monthly_cost: data.total_monthly_cost || data.base_price + (data.active_workers * (data.price_per_worker || 10.00))
      });
    }
  };

  // Filter users
  const filteredUsers = users.filter(user => {
    const matchesSearch = 
      user.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      user.email?.toLowerCase().includes(searchQuery.toLowerCase());
    
    const matchesFilter = 
      activeFilter === 'all' ||
      (activeFilter === 'active' && user.is_active) ||
      (activeFilter === 'inactive' && !user.is_active);
    
    return matchesSearch && matchesFilter;
  });

  const activeCount = users.filter(u => u.is_active).length;
  const inactiveCount = users.filter(u => !u.is_active).length;

  // Add user
  const handleAddUser = async (userData) => {
    const token = localStorage.getItem('streakfree_token');
    const res = await fetch(`${API_URL}/api/team/users`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(userData)
    });
    
    if (!res.ok) {
      const error = await res.json();
      throw new Error(error.detail || 'Failed to add user');
    }
    
    await refreshData();
  };

  // Edit user details (name/email/role)
  const handleEditUser = async (updates) => {
    const token = localStorage.getItem('streakfree_token');
    const res = await fetch(`${API_URL}/api/team/users/${editingUser.id}`, {
      method: 'PUT',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(updates)
    });
    if (!res.ok) {
      const err = await res.json();
      throw new Error(err.detail || 'Failed to update user');
    }
    await refreshData();
    // Sync the open stats modal with the new values so the next render shows them
    if (selectedUser && selectedUser.id === editingUser.id) {
      setSelectedUser({ ...selectedUser, ...updates });
    }
  };

  // Toggle user active status
  const handleToggleActive = async (userId, isActive) => {
    setUpdatingUser(userId);
    try {
      const token = localStorage.getItem('streakfree_token');
      const res = await fetch(`${API_URL}/api/team/users/${userId}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ is_active: isActive })
      });
      
      if (res.ok) {
        await refreshData();
      } else {
        const error = await res.json();
        alert(error.detail || 'Failed to update user');
      }
    } catch (err) {
      console.error('Error toggling user:', err);
    } finally {
      setUpdatingUser(null);
    }
  };

  // Increase user limit
  const handleIncreaseLimit = async (newLimit) => {
    try {
      const token = localStorage.getItem('streakfree_token');
      const res = await fetch(`${API_URL}/api/team/users/limit?max_users=${newLimit}`, {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (res.ok) {
        const data = await res.json();
        setSubscription(prev => ({
          ...prev,
          max_users: data.max_users
        }));
        alert(`User limit increased to ${data.max_users}. New monthly cost: £${data.monthly_cost.toFixed(2)}`);
      } else {
        alert('Failed to update user limit');
      }
    } catch (err) {
      console.error('Error updating limit:', err);
      alert('Error updating limit');
    }
  };

  // View user stats
  const handleViewStats = (user) => {
    setSelectedUser(user);
  };

  // Get tier display name
  const getTierName = (tier) => {
    const tierNames = {
      'starter': 'Tier 1',
      'growth': 'Tier 2',
      'professional': 'Tier 3',
      'enterprise': 'Tier 4'
    };
    return tierNames[tier] || 'Tier 1';
  };

  return (
    <div className="h-full flex flex-col bg-zinc-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 p-4">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h1 className="text-xl font-bold text-gray-900">Users</h1>
            <p className="text-sm text-gray-500">
              Manage your team members and permissions
            </p>
          </div>
          <div className="text-right">
            <p className="text-sm text-gray-500">Monthly cost</p>
            <p className="text-xl font-bold text-emerald-600" data-testid="total-monthly-cost">
              £{subscription.total_monthly_cost.toFixed(2)}
            </p>
            <div className="text-xs text-gray-400">
              <span>Base: £{subscription.base_price.toFixed(2)}</span>
              {subscription.active_workers > 0 && (
                <span> + {subscription.active_workers} worker{subscription.active_workers !== 1 ? 's' : ''} × £{(subscription.price_per_worker || 10).toFixed(2)}</span>
              )}
            </div>
          </div>
        </div>
        
        {/* User Summary with Increase Limit */}
        <div className="p-4 bg-emerald-50 border border-emerald-200 rounded-xl mb-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 bg-emerald-100 rounded-full flex items-center justify-center">
                <UsersIcon size={24} className="text-emerald-600" />
              </div>
              <div>
                <p className="text-lg font-semibold text-gray-900">
                  {subscription.active_users} of {subscription.max_users} users active
                </p>
                <p className="text-sm text-gray-500">
                  {getTierName(subscription.tier)} Plan (£{subscription.base_price.toFixed(2)}/month base)
                </p>
              </div>
            </div>
            <Button
              variant="outline"
              className="border-emerald-500 text-emerald-700 hover:bg-emerald-100"
              onClick={() => {
                const newLimit = prompt(`Current limit: ${subscription.max_users} users\n\nEnter new user limit:`, subscription.max_users + 1);
                if (newLimit && !isNaN(parseInt(newLimit))) {
                  handleIncreaseLimit(parseInt(newLimit));
                }
              }}
              data-testid="increase-limit-btn"
            >
              Increase Limit
            </Button>
          </div>
        </div>
        
        {/* Search */}
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={18} />
          <Input
            placeholder="Search users..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10"
          />
        </div>
      </div>
      
      {/* Tabs */}
      <div className="bg-white border-b border-gray-200 px-4">
        <div className="flex gap-1">
          <button
            onClick={() => setActiveFilter('active')}
            className={`px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
              activeFilter === 'active'
                ? 'border-emerald-500 text-emerald-600'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            ACTIVE ({activeCount})
          </button>
          <button
            onClick={() => setActiveFilter('inactive')}
            className={`px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
              activeFilter === 'inactive'
                ? 'border-emerald-500 text-emerald-600'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            INACTIVE ({inactiveCount})
          </button>
          <button
            onClick={() => setActiveFilter('all')}
            className={`px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
              activeFilter === 'all'
                ? 'border-emerald-500 text-emerald-600'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            ALL ({users.length})
          </button>
        </div>
      </div>
      
      {/* Tip about clicking users */}
      <div className="px-4 py-2 bg-blue-50 border-b border-blue-100 text-sm text-blue-700">
        Click on a user to view their performance stats and link them to job assignees.
      </div>
      
      {/* User List */}
      <div className="flex-1 overflow-y-auto">
        {isLoading ? (
          <div className="flex items-center justify-center h-40">
            <Loader2 className="w-8 h-8 animate-spin text-emerald-500" />
          </div>
        ) : filteredUsers.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-40 text-gray-400">
            <User size={32} className="mb-2" />
            <p>No users found</p>
          </div>
        ) : (
          filteredUsers.map(user => (
            <UserCard
              key={user.id}
              user={user}
              onViewStats={handleViewStats}
              onToggleActive={handleToggleActive}
              isUpdating={updatingUser === user.id}
            />
          ))
        )}
      </div>
      
      {/* Add User Buttons */}
      <div className="bg-white border-t border-gray-200 p-4 flex gap-3">
        <Button 
          onClick={() => setShowAddModal(true)}
          className="flex-1 bg-emerald-600 hover:bg-emerald-700"
          data-testid="add-user-btn"
        >
          <Plus size={18} className="mr-2" />
          Add New User
        </Button>
        <Button 
          variant="outline"
          className="flex-1"
        >
          <Mail size={18} className="mr-2" />
          Add Accountant
        </Button>
      </div>
      
      {/* Add User Modal */}
      {showAddModal && (
        <AddUserModal
          onClose={() => setShowAddModal(false)}
          onSave={handleAddUser}
          maxUsers={subscription.max_users}
          currentActive={subscription.active_users}
          roles={roles}
          pricePerWorker={subscription.price_per_worker || 10.00}
        />
      )}
      
      {/* User Stats Modal */}
      {selectedUser && (
        <UserStatsModal
          user={selectedUser}
          onClose={() => setSelectedUser(null)}
          assigneeOptions={assigneeOptions}
          onRefresh={refreshData}
          roles={roles}
          onEdit={(u) => setEditingUser(u)}
        />
      )}

      {/* Edit User Modal */}
      {editingUser && (
        <EditUserModal
          user={editingUser}
          roles={roles}
          onClose={() => setEditingUser(null)}
          onSave={handleEditUser}
        />
      )}
    </div>
  );
};

export default UsersAndTeams;
